// The text of bundles, on demand. The binary trace does not hand the page the bundles'
// text when it opens -- on a bundle-heavy run it is most of the file -- so the worker
// renders a range of issues from the open trace when one is wanted. This cache hides
// all of that: `get(core, bi)` returns {dis, slots} at once when it is known and null
// otherwise, calling back when it lands, so a listing or a tooltip draws what it has
// and redraws once.

const CHUNK = 512;

export class Disasm {
  constructor(model, render, listing) {
    this.model = model;
    this.render = render;            // (key, first, count) -> Promise, or null
    this.listing = listing;          // () -> Promise of the whole text, or null (unused for traces)
    this.chunks = new Map();         // `${key}:${chunk}` -> {dis: [], slots: [[]...]} | Promise
    this.listeners = new Set();
  }

  onLoad(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }

  // Whether text is inline in the model (unused for traces; the worker renders on demand).
  inline(core) { return core.slotOff.length > 0 && core.dis.length === core.n; }

  get(core, bi) {
    if (bi < 0 || bi >= core.n) return null;
    if (this.inline(core)) return { dis: this.model.disOf(core, bi), slots: this.model.slotsOf(core, bi) };
    if (this.all || !this.render) return null;   // the whole table is on its way: wait for it
    const ch = Math.floor(bi / CHUNK), key = `${core.key}:${ch}`;
    const got = this.chunks.get(key);
    if (got && !(got instanceof Promise)) {
      const j = bi - ch * CHUNK;
      return j < got.dis.length ? { dis: got.dis[j], slots: got.slots[j] } : null;
    }
    if (!got) this.fetch(core, ch, key);
    return null;
  }

  // Several bundles at once (a listing): what is known now; the rest arrives later.
  prefetch(core, from, to) {
    if (this.inline(core)) return;
    if (this.listing) { this.whole(); return; }
    if (!this.render) return;
    for (let ch = Math.floor(from / CHUNK); ch <= Math.floor(Math.max(from, to - 1) / CHUNK); ch++) {
      const key = `${core.key}:${ch}`;
      if (!this.chunks.has(key)) this.fetch(core, ch, key);
    }
  }

  // The whole text table, once: after it lands every core is inline again.
  whole() {
    if (this.all) return this.all;
    this.all = this.listing().then((text) => {
      this.model.applyListing(text);
      this.chunks.clear();
      for (const fn of this.listeners) fn(null, 0, 0);
    }).catch((e) => { console.warn("listing failed", e); });
    return this.all;
  }

  fetch(core, ch, key) {
    const first = ch * CHUNK, count = Math.min(CHUNK, core.n - first);
    if (count <= 0) return;
    const p = this.render(core.key, first, count).then((r) => {
      const slots = [];
      for (let i = 0; i < r.n; i++) {
        const s = [];
        for (let j = r.slotOff[i]; j < r.slotOff[i + 1]; j++) {
          // A slot is its unit, its text, and (from a trace, which stores them apart)
          // the mnemonic and operands, so nothing here has to parse the text.
          s.push(r.slotMn ? { u: r.slotUnit[j], i: r.slotText[j], mn: r.slotMn[j], ops: r.ops.slice(r.opsOff[j], r.opsOff[j + 1]) }
            : { u: r.slotUnit[j], i: r.slotText[j] });
        }
        slots.push(s);
      }
      this.chunks.set(key, { dis: r.dis, slots });
      for (const fn of this.listeners) fn(core, first, r.n);
    }).catch((e) => { console.warn("render failed", e); this.chunks.delete(key); });
    this.chunks.set(key, p);
  }
}
